import { Injectable } from '@angular/core';
import { AnalyticsService } from './analytics.service';
import {
  ActionPerformed,
  PushNotificationSchema,
} from '@capacitor/push-notifications';
import { App, AppState } from '@capacitor/app';

export interface NotificationPayload {
  notification_id: string;
  title: string;
  body?: string;
  type: string;
  deep_link?: string;
  timestamp: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationAnalyticsService {
  private isInitialized = false;
  private eventQueue: Array<{
    mixpanelName: string;
    gaName: string;
    params: any;
  }> = [];
  private currentAppState: 'foreground' | 'background' | 'terminated' =
    'terminated';

  constructor(private analyticsService: AnalyticsService) {
    this.init();
  }

  private async init() {
    try {
      App.addListener('appStateChange', (state: AppState) => {
        this.currentAppState = state.isActive ? 'foreground' : 'background';
      });
      const state = await App.getState();
      if (state.isActive) {
        this.currentAppState = 'foreground';
      }
    } catch (e) {
      console.warn('Failed to get App state', e);
    }
    setTimeout(() => {
      this.isInitialized = true;
      this.flushQueue();
    }, 1000);
  }

  /**
   * Tracks when a push notification is received on device
   */
  trackNotificationReceived(notification: PushNotificationSchema) {
    try {
      const extractedData = this.extractData(notification.data);

      const params = {
        notification_id: notification.id || 'unknown',
        title: notification.title || '',
        body: notification.body || '',
        type: extractedData.type,
        deep_link: extractedData.deep_link,
        received_in_state: this.currentAppState,
        timestamp: new Date().toISOString(),
        ...extractedData,
      };
      this.track('notification_received', 'Notification Received', params);
    } catch (error) {
      console.error('trackNotificationReceived error', error);
    }
  }

  /**
   * Tracks when user taps/opens a push notification
   */
  trackNotificationOpened(action: ActionPerformed) {
    try {
      const notification = action.notification;
      const extractedData = this.extractData(notification.data);

      const params = {
        notification_id: notification.id || 'unknown',
        title: notification.title || '',
        type: extractedData.type,
        deep_link: extractedData.deep_link,
        opened_from_state: this.currentAppState,
        timestamp: new Date().toISOString(),
        ...extractedData,
      };

      this.track('notification_opened', 'Notification Opened', params);
    } catch (error) {
      console.error('trackNotificationOpened error', error);
    }
  }

  /**
   * Tracks when the user grants push notification permission
   */
  trackPermissionGranted() {
    try {
      const params = { timestamp: new Date().toISOString() };
      this.track(
        'notification_permission_granted',
        'Notification Permission Granted',
        params
      );
    } catch (error) {
      console.error('trackPermissionGranted error', error);
    }
  }

  /**
   * Tracks when push notification permission is denied.
   * source: 'prompt' = user just denied the OS prompt
   *         'pre_existing' = permission was already denied on app start
   */
  trackPermissionDenied(source: 'prompt' | 'pre_existing') {
    try {
      const params = { source, timestamp: new Date().toISOString() };
      this.track(
        'notification_permission_denied',
        'Notification Permission Denied',
        params
      );
    } catch (error) {
      console.error('trackPermissionDenied error', error);
    }
  }

  /**
   * Tracks when user interacts with an action button on the notification
   */
  trackNotificationActionClicked(action: ActionPerformed) {
    try {
      const notification = action.notification;
      const extractedData = this.extractData(notification.data);

      const params = {
        notification_id: notification.id || 'unknown',
        action_id: action.actionId,
        action_label: action.inputValue || action.actionId,
        type: extractedData.type,
        timestamp: new Date().toISOString(),
        ...extractedData,
      };

      this.track(
        'notification_action_clicked',
        'Notification Action Clicked',
        params
      );
    } catch (error) {
      console.error('trackNotificationActionClicked error', error);
    }
  }

  /**
   * Extract additional data like type, deep_link and UTM parameters safely
   */
  private extractData(data: any): {
    type: string;
    deep_link?: string;
    [key: string]: any;
  } {
    if (!data) return { type: 'unknown' };

    const type = data?.type || 'unknown';
    const deep_link =
      data?.type_id || data?.deep_link || data?.link || undefined;
    const utm_campaign = data?.utm_campaign;
    const utm_source = data?.utm_source;
    const utm_medium = data?.utm_medium;

    return { type, deep_link, utm_campaign, utm_source, utm_medium };
  }

  /**
   * Wraps tracking and queues events if SDK isn't ready
   */
  private track(GAEventName: string, MixpanelEventName: string, params: any) {
    if (!this.isInitialized) {
      this.eventQueue.push({
        mixpanelName: MixpanelEventName,
        gaName: GAEventName,
        params,
      });
      return;
    }
    this.analyticsService.trackUnified(GAEventName, {
      legacy_event_name: MixpanelEventName,
      ...params,
    });
  }

  private flushQueue() {
    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift();
      if (event) {
        try {
          this.analyticsService.trackUnified(event.gaName, {
            legacy_event_name: event.mixpanelName,
            ...event.params,
          });
        } catch (error) {
          console.error(`Failed to track queued`, error);
        }
      }
    }
  }
}
